import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Watch, RefreshCw, Check, AlertCircle, 
  X, ChevronDown, ChevronUp, Shield, Wifi, WifiOff, Clock, Heart,
  Moon, Footprints, Activity, Droplets, Info, Upload, FileText,
  Building2, Stethoscope, Brain, Scale
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== PROVIDER DEFINITIONS ==============

const CATEGORIES = {
  WEARABLES: 'wearables',
  SMART_DEVICES: 'smart_devices',
  LAB_PROVIDERS: 'lab_providers',
  IMAGING: 'imaging',
  CARE_PROVIDER: 'care_provider',
};

const SPIKE_WEARABLES = [
  { id: 'apple', name: 'Apple Health', icon: '🍎', description: 'iPhone & Apple Watch', platform: 'ios', tier: 'primary', supportsGlucose: false },
  { id: 'health_connect', name: 'Google Health Connect', icon: '💚', description: 'Android devices', platform: 'android', tier: 'primary', supportsGlucose: false },
  { id: 'fitbit', name: 'Fitbit', icon: '⌚', description: 'Fitbit devices & Premium', platform: 'all', tier: 'primary', supportsGlucose: false },
  { id: 'garmin', name: 'Garmin', icon: '⌚', description: 'Garmin watches & trackers', platform: 'all', tier: 'primary', supportsGlucose: false },
  { id: 'oura', name: 'Oura Ring', icon: '💍', description: 'Oura Ring Gen 2 & 3', platform: 'all', tier: 'primary', supportsGlucose: false },
  { id: 'whoop', name: 'Whoop', icon: '🏋️', description: 'Whoop strap', platform: 'all', tier: 'secondary', supportsGlucose: false },
  { id: 'dexcom', name: 'Dexcom', icon: '📊', description: 'Continuous glucose monitor', platform: 'all', tier: 'secondary', supportsGlucose: true },
  { id: 'freestyle_libre', name: 'FreeStyle Libre', icon: '📊', description: 'Abbott glucose sensor', platform: 'all', tier: 'secondary', supportsGlucose: true },
  { id: 'samsung_health_data', name: 'Samsung Health', icon: '📱', description: 'Galaxy Watch & phones', platform: 'all', tier: 'secondary', supportsGlucose: false },
  { id: 'polar', name: 'Polar', icon: '❄️', description: 'Polar watches & sensors', platform: 'all', tier: 'optional', supportsGlucose: false },
  { id: 'strava', name: 'Strava', icon: '🚴', description: 'Activity tracking', platform: 'all', tier: 'optional', supportsGlucose: false },
  { id: 'coros', name: 'Coros', icon: '🏃', description: 'Coros GPS watches', platform: 'all', tier: 'optional', supportsGlucose: false },
  { id: 'ultrahuman', name: 'Ultrahuman', icon: '🔬', description: 'Metabolic tracking ring', platform: 'all', tier: 'optional', supportsGlucose: true },
];

const SMART_DEVICES = [
  { id: 'renpho', name: 'Renpho', icon: '⚖️', description: 'Smart scales & body composition', category: 'scale', metrics: ['Weight', 'BMI', 'Body Fat', 'Visceral Fat'] },
  { id: 'withings', name: 'Withings', icon: '⚖️', description: 'Smart scales, BPM, sleep tracker', category: 'multi', metrics: ['Weight', 'BP', 'Sleep', 'ECG'] },
  { id: 'omron', name: 'Omron', icon: '💗', description: 'Blood pressure monitors', category: 'bp', metrics: ['Systolic', 'Diastolic', 'Pulse'] },
];

const LAB_PROVIDERS = [
  { id: 'quest', name: 'Quest Diagnostics', icon: '🧪', description: 'Lab results via MyQuest portal' },
  { id: 'labcorp', name: 'Labcorp', icon: '🧪', description: 'Lab results via Labcorp Patient' },
  { id: 'manual_lab', name: 'Manual Entry', icon: '📝', description: 'Enter lab results manually' },
];

const IMAGING_OPTIONS = [
  { id: 'mri_upload', name: 'Upload MRI/Imaging', icon: '🧠', description: 'Upload PDF or DICOM files' },
  { id: 'radiology_connect', name: 'Connect Radiology Provider', icon: '🏥', description: 'Link your imaging center' },
  { id: 'manual_imaging', name: 'Manual Entry', icon: '📝', description: 'Enter imaging results manually' },
];

const CARE_PROVIDERS = [
  { id: 'ehr_connect', name: 'Connect EHR', icon: '🏥', description: 'Link your doctor\'s health records' },
  { id: 'share_summary', name: 'Share HealthLink Summary', icon: '📋', description: 'Send your health snapshot to a provider' },
];

const CONNECTION_STATES = {
  NOT_CONNECTED: 'not_connected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
};

// ============== SUB-COMPONENTS ==============

const DeviceCard = ({ device, state, onConnect, disabled }) => {
  const isConnected = state === CONNECTION_STATES.CONNECTED;
  const isConnecting = state === CONNECTION_STATES.CONNECTING;

  return (
    <Card
      className={`p-3 transition-all ${
        isConnected ? 'border-[#8BB89F] bg-[#8BB89F]/5' : 'border-border hover:border-primary/30'
      }`}
      data-testid={`device-${device.id}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-xl">{device.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{device.name}</p>
          <p className="text-[11px] text-muted-foreground truncate">{device.description}</p>
          {device.supportsGlucose && (
            <p className="text-[10px] text-[#F8B64C] flex items-center gap-0.5 mt-0.5">
              <Droplets size={9} /> CGM
            </p>
          )}
          {device.metrics && (
            <p className="text-[10px] text-muted-foreground mt-0.5">{device.metrics.join(' · ')}</p>
          )}
        </div>
        {isConnecting ? (
          <RefreshCw size={16} className="text-primary animate-spin shrink-0" />
        ) : isConnected ? (
          <div className="p-1 rounded-full bg-[#8BB89F]/20"><Check size={14} className="text-[#8BB89F]" /></div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => onConnect(device)} disabled={disabled} className="rounded-full text-xs shrink-0">
            Connect
          </Button>
        )}
      </div>
    </Card>
  );
};

const CategorySection = ({ title, subtitle, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="mb-5">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full py-2"
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <Icon size={14} className="text-primary" />
          </div>
          <div className="text-left">
            <h2 className="text-sm font-semibold">{title}</h2>
            {subtitle && <p className="text-[10px] text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="space-y-2 pt-2">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// ============== PERMISSION MODAL ==============

const PermissionsModal = ({ device, onConfirm, onCancel }) => {
  const metricGroups = [
    { icon: Heart, label: 'Heart Rate & HRV', items: ['Heart rate', 'Resting HR', 'HRV'] },
    { icon: Moon, label: 'Sleep Data', items: ['Duration', 'Deep sleep', 'REM', 'Light sleep'] },
    { icon: Footprints, label: 'Activity', items: ['Steps', 'Calories burned'] },
    { icon: Activity, label: 'Vitals', items: ['Blood oxygen (SpO2)'] },
  ];
  if (device.supportsGlucose) {
    metricGroups.push({ icon: Droplets, label: 'Glucose', items: ['Continuous glucose'] });
  }

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        className="bg-card rounded-3xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="permissions-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Connect {device.name}</h3>
          <button onClick={onCancel} className="p-1 rounded-full hover:bg-muted"><X size={20} /></button>
        </div>
        <div className="flex items-center gap-2 mb-4 p-3 rounded-xl bg-primary/5">
          <Shield size={16} className="text-primary" />
          <p className="text-xs text-muted-foreground">Data synced securely via Spike API and encrypted at rest.</p>
        </div>
        <p className="text-sm font-medium mb-3">We will sync:</p>
        <div className="space-y-2 mb-6">
          {metricGroups.map((g) => (
            <div key={g.label} className="flex items-start gap-3 p-3 rounded-xl bg-muted/30">
              <g.icon size={16} className="text-primary mt-0.5" />
              <div>
                <p className="text-sm font-medium">{g.label}</p>
                <p className="text-xs text-muted-foreground">{g.items.join(' · ')}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="flex-1 rounded-full">Cancel</Button>
          <Button onClick={onConfirm} className="flex-1 rounded-full" data-testid="confirm-connect-btn">Connect</Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============== UPLOAD MODAL ==============

const UploadModal = ({ title, onClose }) => {
  const [file, setFile] = useState(null);

  const handleUpload = () => {
    if (!file) { toast.error('Please select a file'); return; }
    toast.success(`${file.name} uploaded! We'll process it shortly.`);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
        className="bg-card rounded-3xl p-6 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
        data-testid="upload-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-muted"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div className="border-2 border-dashed border-border rounded-2xl p-8 text-center cursor-pointer hover:border-primary/30 transition-colors"
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            <Upload size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-medium">{file ? file.name : 'Choose a file'}</p>
            <p className="text-xs text-muted-foreground mt-1">PDF, DICOM, JPG, PNG</p>
            <input
              id="file-upload"
              type="file"
              className="hidden"
              accept=".pdf,.dcm,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0])}
              data-testid="file-upload-input"
            />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">Cancel</Button>
            <Button onClick={handleUpload} disabled={!file} className="flex-1 rounded-full" data-testid="upload-btn">Upload</Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============== MAIN COMPONENT ==============

export default function DeviceConnectionPage() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [connections, setConnections] = useState({});
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showUpload, setShowUpload] = useState(null);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleConnect = (device) => {
    // Wearables go through permission flow
    if (SPIKE_WEARABLES.find(w => w.id === device.id)) {
      setSelectedDevice(device);
    } else {
      // Smart devices / lab / care — simulate connection
      simulateConnect(device);
    }
  };

  const simulateConnect = async (device) => {
    setConnections(prev => ({ ...prev, [device.id]: CONNECTION_STATES.CONNECTING }));
    try {
      await axios.post(`${API}/devices/connect`, { provider: device.id, metrics: [] }).catch(() => {});
      await new Promise(r => setTimeout(r, 1500));
      setConnections(prev => ({ ...prev, [device.id]: CONNECTION_STATES.CONNECTED }));
      toast.success(`${device.name} connected!`);
    } catch {
      setConnections(prev => ({ ...prev, [device.id]: CONNECTION_STATES.NOT_CONNECTED }));
      toast.error('Connection failed');
    }
  };

  const confirmConnect = async () => {
    if (!selectedDevice) return;
    const device = selectedDevice;
    setSelectedDevice(null);
    setConnections(prev => ({ ...prev, [device.id]: CONNECTION_STATES.CONNECTING }));
    try {
      await axios.post(`${API}/devices/connect`, { provider: device.id, metrics: [] }).catch(() => {});
      await new Promise(r => setTimeout(r, 2000));
      setConnections(prev => ({ ...prev, [device.id]: CONNECTION_STATES.CONNECTED }));
      updateUser({ device_synced: true, device_type: device.id });
      toast.success(`${device.name} connected!`);
    } catch {
      setConnections(prev => ({ ...prev, [device.id]: CONNECTION_STATES.NOT_CONNECTED }));
      toast.error('Connection failed');
    }
  };

  const handleSkip = () => {
    toast.info('You can connect devices later from Settings');
    navigate('/dashboard');
  };

  const connectedCount = Object.values(connections).filter(s => s === CONNECTION_STATES.CONNECTED).length;

  const primaryWearables = SPIKE_WEARABLES.filter(d => d.tier === 'primary');
  const secondaryWearables = SPIKE_WEARABLES.filter(d => d.tier === 'secondary');
  const optionalWearables = SPIKE_WEARABLES.filter(d => d.tier === 'optional');

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="device-connection-page">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
            <ArrowLeft size={20} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowSkipConfirm(true)} className="text-muted-foreground text-xs" data-testid="skip-top-btn">
            Skip for now
          </Button>
        </div>
      </header>

      <main className="px-5 py-6 max-w-lg mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
          <h1 className="text-2xl font-heading font-bold mb-2">Connect Your Data</h1>
          <p className="text-muted-foreground text-sm">Link devices, labs, and providers to power your Digital Twin.</p>
          {connectedCount > 0 && (
            <Badge className="mt-2 bg-[#8BB89F]/10 text-[#8BB89F] border-0">
              <Check size={12} className="mr-1" />{connectedCount} connected
            </Badge>
          )}
        </motion.div>

        {/* 1. WEARABLES (Spike API) */}
        <CategorySection title="Wearables" subtitle="Via Spike API" icon={Watch} defaultOpen={true}>
          {primaryWearables.map(d => (
            <DeviceCard key={d.id} device={d} state={connections[d.id] || CONNECTION_STATES.NOT_CONNECTED} onConnect={handleConnect} disabled={isLoading} />
          ))}
          {secondaryWearables.length > 0 && (
            <div className="pt-1">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 mt-2">More Devices</p>
              {secondaryWearables.map(d => (
                <div key={d.id} className="mb-2">
                  <DeviceCard device={d} state={connections[d.id] || CONNECTION_STATES.NOT_CONNECTED} onConnect={handleConnect} disabled={isLoading} />
                </div>
              ))}
            </div>
          )}
          {optionalWearables.length > 0 && (
            <CategorySection title={`Additional (${optionalWearables.length})`} icon={Activity} defaultOpen={false}>
              {optionalWearables.map(d => (
                <DeviceCard key={d.id} device={d} state={connections[d.id] || CONNECTION_STATES.NOT_CONNECTED} onConnect={handleConnect} disabled={isLoading} />
              ))}
            </CategorySection>
          )}
        </CategorySection>

        {/* 2. SMART DEVICES (Rogue) */}
        <CategorySection title="Smart Devices" subtitle="Renpho, Withings, Omron" icon={Scale} defaultOpen={false}>
          {SMART_DEVICES.map(d => (
            <DeviceCard key={d.id} device={d} state={connections[d.id] || CONNECTION_STATES.NOT_CONNECTED} onConnect={handleConnect} disabled={isLoading} />
          ))}
          <Card className="p-3 bg-[#F8B64C]/5 border-[#F8B64C]/20">
            <div className="flex items-start gap-2">
              <Info size={14} className="text-[#F8B64C] mt-0.5 shrink-0" />
              <p className="text-[11px] text-muted-foreground">These devices connect via their own APIs. Some data may require manual sync.</p>
            </div>
          </Card>
        </CategorySection>

        {/* 3. LAB PROVIDERS */}
        <CategorySection title="Lab Providers" subtitle="Blood work & diagnostics" icon={Stethoscope} defaultOpen={false}>
          {LAB_PROVIDERS.map(d => (
            <DeviceCard
              key={d.id}
              device={d}
              state={connections[d.id] || CONNECTION_STATES.NOT_CONNECTED}
              onConnect={d.id === 'manual_lab' ? () => setShowUpload('lab') : handleConnect}
              disabled={isLoading}
            />
          ))}
        </CategorySection>

        {/* 4. IMAGING / MRI */}
        <CategorySection title="Imaging & MRI" subtitle="Upload or connect" icon={Brain} defaultOpen={false}>
          {IMAGING_OPTIONS.map(d => (
            <DeviceCard
              key={d.id}
              device={d}
              state={connections[d.id] || CONNECTION_STATES.NOT_CONNECTED}
              onConnect={d.id.includes('upload') || d.id.includes('manual') ? () => setShowUpload('imaging') : handleConnect}
              disabled={isLoading}
            />
          ))}
        </CategorySection>

        {/* 5. CARE PROVIDER */}
        <CategorySection title="Care Provider" subtitle="Share data with your doctor" icon={Building2} defaultOpen={false}>
          {CARE_PROVIDERS.map(d => (
            <DeviceCard
              key={d.id}
              device={d}
              state={connections[d.id] || CONNECTION_STATES.NOT_CONNECTED}
              onConnect={d.id === 'share_summary' ? () => navigate('/care') : handleConnect}
              disabled={isLoading}
            />
          ))}
        </CategorySection>

        {/* No wearable info */}
        <Card className="glass-card p-4 bg-[#F8B64C]/5 border-[#F8B64C]/20 mb-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#F8B64C]/10 shrink-0">
              <Info size={16} className="text-[#F8B64C]" />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">No device?</span> Use our{' '}
              <span className="text-primary font-medium cursor-pointer" onClick={() => navigate('/scan/start')}>Facial Scan</span>{' '}
              or manually enter data. You can connect more sources anytime.
            </p>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-3">
          {connectedCount > 0 ? (
            <Button onClick={() => navigate('/dashboard')} className="w-full rounded-full h-12" data-testid="continue-btn">
              Continue to Dashboard <ArrowRight size={18} className="ml-2" />
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => setShowSkipConfirm(true)} className="w-full text-muted-foreground" data-testid="skip-bottom-btn">
              Skip for now
            </Button>
          )}
        </div>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {selectedDevice && (
          <PermissionsModal device={selectedDevice} onConfirm={confirmConnect} onCancel={() => setSelectedDevice(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showUpload && (
          <UploadModal title={showUpload === 'lab' ? 'Upload Lab Results' : 'Upload Imaging'} onClose={() => setShowUpload(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSkipConfirm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSkipConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-card rounded-3xl p-6 w-full max-w-sm"
              onClick={(e) => e.stopPropagation()}
              data-testid="skip-confirmation-modal"
            >
              <div className="text-center mb-4">
                <div className="w-12 h-12 rounded-full bg-[#F8B64C]/10 flex items-center justify-center mx-auto mb-3">
                  <AlertCircle size={24} className="text-[#F8B64C]" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">Skip Device Connection?</h3>
                <p className="text-sm text-muted-foreground">
                  Your Digital Twin confidence will be <span className="text-[#F8B64C] font-medium">limited</span> until data sources are connected.
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowSkipConfirm(false)} className="flex-1 rounded-full">Go Back</Button>
                <Button onClick={handleSkip} variant="secondary" className="flex-1 rounded-full" data-testid="confirm-skip-btn">Skip</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
